import { cloneElement, useEffect, useState } from 'react';
import classNames from 'classnames';
import { Col, Container, Row } from 'react-bootstrap';
import { useBookmarkLoader, usePermalinkLoader, usePrevious, useSearchPageBookmarkPermalink } from '@medifind/hooks';
import { IconArrowSmallBack } from '@medifind/icons';
import { IconRestart } from '@medifind/icons';
import { IconEditSmall } from '@medifind/icons';
import { clinicalTrialSearch, getCodingByID } from '@medifind/interface';
import { Share } from '@medifind/popups';
import { changeURLParams } from '@medifind/router';
import { useSortChanger } from '@medifind/router';
import { usePageChanger, usePageHooks, useRouter } from '@medifind/router';
import { DropDown } from '@medifind/search-results';
import { Bookmark } from '@medifind/shared-basic-components';
import { Button } from '@medifind/shared-basic-components';
import { isPrerender } from '@medifind/utils';
import { numberWithCommas } from '@medifind/utils';
import {
  areFilterStatesEqual,
  clearTrialFilters,
  logEventGA4,
  setClinicalTrialStepper,
  trialFiltersDependencies,
  updateTrialConditionFilterOption,
  useClinicalTrialStepper,
  useFilterContext,
  useLocationStore,
  useTrialConditionFilter,
  useTrialCriteriaFilter,
  useTrialInterventionTypeFilter,
  useTrialPhaseFilter,
  useTrialRadiusFilter,
  useTrialStatusFilter,
  withFilters,
} from '@medifind/zustand';
import { findNextValidStep } from '../../utils/stepperUtils';
import sharedStyles from './../Steppers.Shared.module.scss';
import Results from './Results';

// components
import AgeStep from './steps/AgeStep';
import ConditionStep from './steps/ConditionStep';
import DistanceStep from './steps/DistanceStep';
import GenderStep from './steps/GenderStep';
import LocationStep from './steps/LocationStep';
import PersonalizeStep from './steps/PersonalizeStep';
import RecruitingStep from './steps/RecruitingStep';
import RefineResultsStep from './steps/RefineResultsStep';
import styles from './TrialSearchShared.module.scss';

const ClinicalTrialStepper = () => {
  usePageHooks();
  const filters = useFilterContext();
  const router = useRouter();
  const { query: queryParams } = router;
  const { projectId, step: queryStep, sort: querySort } = queryParams;
  const step = queryStep || 0;
  const previousStep = usePrevious(step);

  const [results, setResults] = useState(null);
  const [size] = useState(20);
  const [loading, setLoading] = useState(false);

  const stepWorkflow = {
    condition: {
      stepLabel: 'Condition',
      key: 'condition',
      stepComponent: <ConditionStep />,
      isRequired: true,
    },
    refineResults: {
      stepLabel: 'Refine',
      key: 'refineResults',
      stepComponent: <RefineResultsStep />,
      isRequired: false,
      optional: true,
    },
    recruiting: {
      stepLabel: 'Recruiting',
      key: 'recruiting',
      stepComponent: <RecruitingStep />,
      isRequired: false,
      optional: true,
    },
    location: {
      stepLabel: 'Location',
      key: 'location',
      stepComponent: <LocationStep />,
      isRequired: false,
      optional: true,
    },
    distance: {
      stepLabel: 'Distance',
      key: 'distance',
      stepComponent: <DistanceStep />,
      isRequired: false,
      optional: true,
    },
    age: {
      stepLabel: 'Age',
      key: 'age',
      stepComponent: <AgeStep />,
      isRequired: false,
      optional: true,
    },
    gender: {
      stepLabel: 'Sex',
      key: 'gender',
      stepComponent: <GenderStep />,
      isRequired: false,
      optional: true,
    },
    answeredQuestions: {
      stepLabel: 'Questions',
      key: 'answeredQuestions',
      stepComponent: <PersonalizeStep />,
      isRequired: false,
      optional: true,
    },
    // contact: {
    //   stepLabel: 'Contact',
    //   key: 'contact',
    //   stepComponent: <ContactStep />,
    //   isRequired: false,
    //   optional: true,
    // },
  };

  const stepsDataKeys = Object.keys(stepWorkflow ? stepWorkflow : {});

  const initialState = stepsDataKeys.map((key) => ({
    key,
    data: null,
    skip: false,
    isRequired: stepWorkflow[key].isRequired,
    stepLabel: stepWorkflow[key].stepLabel,
  }));

  const steps = useClinicalTrialStepper((state) => state.steps);
  const location = useLocationStore();

  const { sort, handleSortChange, changeSortManualURLModification } = useSortChanger('sort', 'date');
  const { page } = usePageChanger('page', [...filters.valueDependencies, projectId, location, sort]);

  usePermalinkLoader({
    onLoad: ({ data: { sort, steps } }) => {
      setClinicalTrialStepper({ steps: initialState.map((x) => ({ ...x, ...(steps.find(({ key }) => x.key === key) || {}) })) });
      return { isStarted: true, step: stepsDataKeys.length + 1, ...changeSortManualURLModification(sort) };
    },
    preLoad: ({ data: { steps } }) => {
      const { trialCondition } = filters.values;

      filters.setFilterDependencies([trialCondition], { exclude: ['all'] });
    },
    filterList: filters.filterList,
  });

  useBookmarkLoader({
    onLoad: ({ data: { sort, steps } }) => {
      setClinicalTrialStepper({ steps: initialState.map((x) => ({ ...x, ...(steps.find(({ key }) => x.key === key) || {}) })) });
      return { isStarted: true, step: stepsDataKeys.length + 1, ...changeSortManualURLModification(sort) };
    },
    preLoad: ({ data: { steps } }) => {
      const { trialCondition } = filters.values;
      filters.setFilterDependencies([trialCondition], { exclude: ['all'] });
    },
    filterList: filters.filterList,
  });

  useEffect(() => {
    if (!steps) {
      setClinicalTrialStepper({ steps: initialState });
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      if (projectId) {
        // When coming from conditions page "check for trials"
        const display = (await getCodingByID(projectId))?.display;
        if (display) {
          const data = { display: display, value: projectId };
          updateTrialConditionFilterOption(data);
          const currentSteps = initialState.map((step) => ({ ...step, data: step?.key === 'condition' ? data : step.data }));
          setClinicalTrialStepper({ steps: currentSteps });
          if (!isPrerender) changeURLParams(router, { isStarted: true, step: 1 }, { type: 'replace', clearExistingParams: true });
        }
      }
    };
    load();
  }, [projectId]);

  async function fetchClinicalTrials(query) {
    setLoading(true);
    return clinicalTrialSearch(query)
      .then((results) => setResults(results))
      .catch((error) => {
        !error?.code || (error?.code >= 500 && error?.code <= 599) ? setResults({ error: true }) : setResults(null);
      })
      .finally(() => setLoading(false));
  }

  const { condition, gender, age, answeredQuestions } =
    steps?.reduce((prev, curr) => ({ ...prev, [curr.key]: curr?.data?.value }), {}) || {};

  useEffect(() => {
    /**
     * Only update the dependencies when your on the first step
     */
    if (condition && step === 0) filters.setFilterDependencies([condition]);

    // check if each of the enabled filters' states is equal to its initial state
    if (step > 0 && Object.values(filters.enabledStates).every((state) => areFilterStatesEqual(state.getInitialState(), state)))
      changeURLParams(router, { step: 0 });
  }, [condition, step]);

  useEffect(() => {
    if (steps) {
      const { lat, lon, fidelity, stateProvinceCode, countryCode } = location;
      const { trialCondition, trialRadius, trialStatus, trialPhase, trialInterventionType, trialCriteria } = filters.values;

      const query = {
        projectId: trialCondition || null,
        lat,
        lon,
        fidelity: fidelity || null,
        state: stateProvinceCode || null,
        country: countryCode || null,
        radius: trialRadius,
        answeredQuestions: answeredQuestions ? answeredQuestions : null,
        status: trialStatus,
        gender: gender || null,
        age: age || null,
        size,
        askQuestion: true,
        phase: trialPhase,
        interventionType: trialInterventionType,
        criteria: trialCriteria,
        page: page,
        sortBy: sort,
      };
      if (condition) {
        fetchClinicalTrials(query);
      } else {
        // If no condition found at last step. Start over
        step >= steps?.length ? onStartOver() : setResults(null);
      }
    }
  }, [...filters.dependencies, steps, page, querySort, location]);
  useSearchPageBookmarkPermalink(
    projectId ? 'Clinical Trials Search' : 'Clinical Trials',
    projectId ? 'clinical-trial-search-results' : 'trial-search',
    {
      ...(projectId ? { projectId } : {}),
      steps: steps?.map((d) => ({ key: d.key, data: !!d.data ? d.data : null })),
    },
    {},
    filters.filterList,
    [...filters.dependencies, projectId],
  );

  const handleStepSubmit = ({ data, doNotAdvance }) => {
    const tempSteps = [...steps];
    tempSteps[step] = { ...tempSteps[step], data };

    const { value } = data || { value: '' };
    const { key, stepLabel } = tempSteps[step];
    if (key === 'condition') {
      // Partial reset of the form if the condition changes
      const criteriaStepI = tempSteps.findIndex(({ key }) => key === 'criteria' || key === 'answeredQuestions');
      if (criteriaStepI >= 0) tempSteps[criteriaStepI] = { ...tempSteps[criteriaStepI], data: null };
    }
    setClinicalTrialStepper({ steps: tempSteps });
    if (key === 'refineResults' && value === 'N') {
      logEventGA4('trial_search', { step: stepLabel });
      return changeURLParams(router, { step: tempSteps.length }); // shows results, not contact step
    } else if (!doNotAdvance) {
      logEventGA4('trial_search', { step: stepLabel });
      const nextValidStep = findNextValidStep({ steps: tempSteps, currentStep: queryStep });
      return changeURLParams(router, { step: nextValidStep });
    }
  };

  const onSubmitSuccess = () => {
    handleStepSubmit({ data: { value: true } });
  };

  const getCurrentStep = () => {
    if (queryStep > stepsDataKeys.length - 1 && results) {
      return <Results results={results} showScore={projectId} perPage={size} />;
    }
    const currentStep = stepsDataKeys[step];
    const currentStepData = stepWorkflow[currentStep] || null;
    const { stepComponent, key, skip, optional } = currentStepData || {};
    if (stepComponent) {
      return cloneElement(stepComponent, {
        handleStepSubmit,
        stepKey: key,
        skip,
        trialCount: results?.totalResults,
        onSubmitSuccess: key === 'contact' ? onSubmitSuccess : null,
        showOptional: optional,
        nextQuestion: key === 'answeredQuestions' && results?.nextQuestion,
        loading: loading,
        isBackwards: previousStep < step,
      });
    }
  };

  const onStartOver = () => {
    changeURLParams(router, { isStarted: true, step: 0 }, { clearExistingParams: true });
    setClinicalTrialStepper({ steps: initialState });
    clearTrialFilters();
  };
  const onBack = () => {
    changeURLParams(router, { step: queryStep - 1 });
  };

  const onEditStep = (value) => {
    const tempSteps = [...steps];
    let { key, data } = tempSteps[value];
    if (key === 'answeredQuestions') {
      if (data) {
        data.value = [];
      } else {
        data = { value: [] };
      }
      setClinicalTrialStepper({ steps: tempSteps });
    }
    changeURLParams(router, { step: value });
  };

  const showResults = queryStep > stepsDataKeys.length - 1;
  const trialCount = results?.totalResults;
  const conditionStep = steps && steps.find(({ key }) => key === 'condition');
  const conditionDisplay = filters.states?.trialCondition?.selected?.display || '';
  const resultText = `${numberWithCommas(trialCount)} ${conditionDisplay + (conditionDisplay ? ' ' : '')}Clinical Trial${
    trialCount === 1 ? '' : 's'
  } were found`;

  return (
    <Container className={styles['trial-stepper']}>
      <Row className={styles['top-row']}>
        <Col className={classNames(styles['button-container'], { [styles['button-container--stepper']]: !showResults })}>
          <div className={styles['title-container']}>
            <h1 className={classNames(styles['title'], { [styles['title--results']]: showResults })}>Trial Search</h1>
            {!showResults && conditionDisplay && <div className={styles['sub-title']}>Find {conditionDisplay} Trials</div>}
          </div>
          {showResults && (
            <>
              <div className={styles['results-text-container']}>
                <p>{showResults ? resultText : `${conditionDisplay}`}</p>{' '}
              </div>
              <div className={styles['nav-buttons-row']}>
                <Button
                  type="thin"
                  className={classNames(styles['back-button'], styles['button'])}
                  onClick={onBack}
                  color="light-blue"
                  iconBefore={<IconArrowSmallBack />}
                  label="Back"
                  iconOnlyAt="sm"
                  classes={{ content: styles['button--super-thin'] }}
                />
                <Button
                  type="thin"
                  className={styles['button']}
                  onClick={onStartOver}
                  color="light-blue"
                  iconBefore={<IconRestart />}
                  label="Start Over"
                  iconOnlyAt="sm"
                  classes={{ content: styles['button--super-thin'] }}
                />
                <DropDown
                  noHeader
                  label="Edit Input"
                  iconBefore={<IconEditSmall />}
                  placeholder="Edit Input"
                  classes={{
                    filter: styles['dropdown-filter'],
                    menuWrapper: styles['filter-menu-wrapper'],
                    open: styles['dropdown-open'],
                    menuItem: styles['filter-menu-item'],
                    placeholder: styles['filter-menu-item'],
                    menu: styles['filter-menu'],
                    select: styles['filter-menu-item'],
                  }}
                  onChange={onEditStep}
                  items={steps ? steps.map(({ stepLabel }, index) => ({ display: stepLabel, value: index })) : []}
                  className={classNames(styles['button'], styles['drop-down'])}
                />
              </div>
              <div className={styles['share-buttons-row']}>
                <Bookmark
                  buttonType="thin"
                  iconOnlyAt="xs"
                  color="blue"
                  className={classNames(styles['button'], styles['share-button'])}
                  classes={{ content: styles['button--super-thin'] }}
                />
                <Share
                  id="trial-search-share"
                  iconOnlyAt="xs"
                  buttonType="thin"
                  className={styles['button']}
                  classes={{ content: styles['button--super-thin'] }}
                />
              </div>
            </>
          )}
          {showResults ? null : (
            <progress className={sharedStyles['progress']} max={100} value={(step / (steps ? steps.length : 1)) * 100} />
          )}
        </Col>
      </Row>
      <Row className={styles['step-row']}>
        <Col>{steps?.length > 0 && getCurrentStep()}</Col>
      </Row>
    </Container>
  );
};

export default withFilters(
  [
    useTrialConditionFilter,
    useTrialPhaseFilter,
    useTrialInterventionTypeFilter,
    useTrialStatusFilter,
    useTrialRadiusFilter,
    useTrialCriteriaFilter,
  ],
  [trialFiltersDependencies],
  [clearTrialFilters],
)(ClinicalTrialStepper);
